import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { Brain } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useAuthStore } from '@/app/store/auth'
import api from '@/lib/api/axios'
import { ScenarioConfigTab } from '../components/ScenarioConfigTab'
import { ModelChangesTab } from '../components/ModelChangesTab'
import { SyncHistoryTab } from '../components/SyncHistoryTab'
import { SyncControls } from '../components/SyncControls'

export function AiModelsPage() {
    const { t } = useTranslation()
    const isAuthenticated = useAuthStore((s) => s.isAuthenticated)

    const { data: changesStats } = useQuery({
        queryKey: ['ai-changes-stats'],
        queryFn: async () => {
            const res = await api.get('/api/ai/models/changes/stats')
            return res.data?.stats
        },
        enabled: isAuthenticated,
    })

    const { data: syncLogsData } = useQuery({
        queryKey: ['ai-sync-logs-count'],
        queryFn: async () => {
            const res = await api.get('/api/ai/models/sync/logs', { params: { limit: 1 } })
            return res.data
        },
        enabled: isAuthenticated,
    })

    const changeCount = changesStats?.total || 0
    const syncLogCount = syncLogsData?.total || 0

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Brain className="h-5 w-5 text-primary" />
                </div>
                <div>
                    <h1 className="text-2xl font-semibold tracking-tight">{t('aiModels.title', 'AI Model Management')}</h1>
                    <p className="text-muted-foreground text-sm">{t('aiModels.pageDescription', 'Manage AI vendors, models, and scenario preferences')}</p>
                </div>
            </div>

            {/* Sync trigger + schedule — page level so it is reachable from every tab */}
            <SyncControls />

            {/* Tabs */}
            <Tabs defaultValue="scenario-config">
                <TabsList>
                    <TabsTrigger value="scenario-config" className="gap-1.5">
                        {t('aiModels.tabs.scenarioConfig', 'Scenario Config')}
                        <Badge variant="secondary" className="text-[10px] py-0 px-1.5">{t('aiModels.tabs.mainBadge', 'Main')}</Badge>
                    </TabsTrigger>
                    <TabsTrigger value="model-changes" className="gap-1.5">
                        {t('aiModels.changes.title', 'Model Changes')}
                        {changeCount > 0 && (
                            <Badge variant={changesStats?.critical > 0 ? 'destructive' : 'secondary'} className="text-[10px] py-0 px-1.5">
                                {changeCount}
                            </Badge>
                        )}
                    </TabsTrigger>
                    <TabsTrigger value="sync-history" className="gap-1.5">
                        {t('aiModels.syncHistory.title', 'Sync History')}
                        {syncLogCount > 0 && (
                            <Badge variant="secondary" className="text-[10px] py-0 px-1.5">{syncLogCount}</Badge>
                        )}
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="scenario-config" className="mt-6">
                    <ScenarioConfigTab />
                </TabsContent>

                <TabsContent value="model-changes" className="mt-6">
                    <ModelChangesTab />
                </TabsContent>

                <TabsContent value="sync-history" className="mt-6">
                    <SyncHistoryTab />
                </TabsContent>
            </Tabs>
        </div>
    )
}
